import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress, Button, Collapse } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Simple fare calculator for guest estimation
const calculateFare = (distance, time, carDetails, decimal = 2) => {
  const baseCalculated =
    parseFloat(carDetails.rate_per_unit_distance || 10) * parseFloat(distance) +
    parseFloat(carDetails.rate_per_hour || 200) * (parseFloat(time) / 3600);

  let total = baseCalculated + parseFloat(carDetails.base_fare || 50);
  total = Math.max(total, parseFloat(carDetails.min_fare || 80));

  let convenienceFee = 0;
  if (carDetails.convenience_fee_type === 'flat') {
    convenienceFee = parseFloat(carDetails.convenience_fees || 0);
  } else {
    convenienceFee = (total * parseFloat(carDetails.convenience_fees || 10)) / 100;
  }

  return {
    totalCost: parseFloat(total.toFixed(decimal)),
    grandTotal: parseFloat((total + convenienceFee).toFixed(decimal)),
  };
};

export default function GuestPriceEstimator({ pickup, destination, onSelect }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cartypes = useSelector(state => state.cartypes);
  const settingsdata = useSelector(state => state.settingsdata);
  const settings = useMemo(() => settingsdata?.settings || {}, [settingsdata]);

  const cars = useMemo(() => (cartypes?.cars ? [...cartypes.cars] : []), [cartypes]);

  const [loading, setLoading] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  // Calculate route when pickup and destination change
  useEffect(() => {
    if (!pickup?.coords || !destination?.coords) {
      setRouteInfo(null);
      setEstimates([]);
      return;
    }

    const getRoute = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!window.google) {
          throw new Error('Google Maps not loaded');
        }

        const directionsService = new window.google.maps.DirectionsService();

        const result = await new Promise((resolve, reject) => {
          directionsService.route(
            {
              origin: pickup.coords,
              destination: destination.coords,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (response, status) => {
              if (status === 'OK') {
                resolve(response);
              } else {
                reject(new Error(`Directions failed: ${status}`));
              }
            }
          );
        });

        const route = result.routes[0].legs[0];
        const distanceKm = route.distance.value / 1000;
        const timeSeconds = route.duration.value;

        setRouteInfo({
          distance: distanceKm,
          distanceText: route.distance.text,
          time: timeSeconds,
          timeText: route.duration.text,
        });

        // Calculate estimates for all car types
        const newEstimates = cars.map(car => {
          const distance = settings.convert_to_mile ? distanceKm / 1.609344 : distanceKm;
          const fare = calculateFare(distance, timeSeconds, car, settings.decimal || 2);
          const eta = Math.max(2, Math.floor(Math.random() * 5) + 2); // Simulated ETA 2-7 min

          return {
            ...car,
            ...fare,
            eta,
            distance: parseFloat(distance.toFixed(1)),
          };
        });

        setEstimates(newEstimates);
      } catch (err) {
        console.error('Route calculation error:', err);
        setError(t('route_error') || 'Could not calculate route');
      } finally {
        setLoading(false);
      }
    };

    getRoute();
  }, [pickup, destination, cars, settings, t]);

  useEffect(() => {
    if (!selectedCar && estimates.length > 0) {
      setSelectedCar(estimates[0]);
    }
  }, [estimates, selectedCar]);

  const handleBookNow = () => {
    if (onSelect && selectedCar) {
      onSelect(selectedCar);
    }
    navigate('/login');
  };

  if (!pickup?.coords || !destination?.coords) {
    return null;
  }

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: 2,
          p: 3,
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={24} sx={{ color: '#000' }} />
        <Typography sx={{ color: '#666' }}>
          {t('calculating_price') || 'Calculating price...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: 2,
          p: 3,
          mt: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: '#666' }}>{error}</Typography>
      </Box>
    );
  }

  if (!routeInfo || estimates.length === 0) {
    return null;
  }

  const currency = settings.symbol || 'kr';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 2,
        mt: 2,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}
    >
      {/* Route Summary */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCarIcon sx={{ fontSize: 18, color: '#666' }} />
            <Typography sx={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
              {routeInfo.distanceText}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 18, color: '#666' }} />
            <Typography sx={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
              {routeInfo.timeText}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: '#22c55e',
            color: '#fff',
            px: 1.5,
            py: 0.5,
            borderRadius: '500px',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#fff',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
          {t('live') || 'Live'}
        </Box>
      </Box>

      {/* Selected Car Preview */}
      {selectedCar && (
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#fafafa' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedCar.image && (
              <Box
                component="img"
                src={selectedCar.image}
                alt={selectedCar.name}
                sx={{ width: 56, height: 32, objectFit: 'contain' }}
              />
            )}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#000' }}>
                {selectedCar.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: '#666' }} />
                <Typography sx={{ fontSize: 13, color: '#666' }}>
                  {selectedCar.eta} min
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#000' }}>
              {currency} {selectedCar.grandTotal}
            </Typography>
            {expanded ? (
              <KeyboardArrowUpIcon sx={{ color: '#666' }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ color: '#666' }} />
            )}
          </Box>
        </Box>
      )}

      {/* Expanded Car Options */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: '1px solid #f0f0f0' }}>
          {estimates.map((car) => {
            const isSelected = selectedCar?.name === car.name;
            return (
              <Box
                key={car.name}
                onClick={() => {
                  setSelectedCar(car);
                  setExpanded(false);
                }}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  bgcolor: isSelected ? '#f6f6f6' : '#fff',
                  borderBottom: '1px solid #f0f0f0',
                  '&:hover': { bgcolor: '#f6f6f6' },
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {car.image && (
                    <Box
                      component="img"
                      src={car.image}
                      alt={car.name}
                      sx={{ width: 56, height: 32, objectFit: 'contain' }}
                    />
                  )}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#000' }}>
                        {car.name}
                      </Typography>
                      {isSelected && (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography sx={{ fontSize: 13, color: '#666' }}>
                        {car.eta} min • {car.extra_info || `${car.capacity || 4} seats`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#000' }}>
                  {currency} {car.grandTotal}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>

      {/* Book Now Button */}
      <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleBookNow}
          sx={{
            bgcolor: '#000',
            color: '#fff',
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              bgcolor: '#333',
            },
          }}
        >
          {t('book_now') || 'Book now'} — {currency} {selectedCar?.grandTotal}
        </Button>
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: 12,
            color: '#666',
            mt: 1.5,
          }}
        >
          {t('login_to_book') || 'Sign in to complete your booking'}
        </Typography>
      </Box>
    </Box>
  );
}
